import React, { useEffect, useRef, useState, useCallback } from 'react';
import '../../../styles/components/WebDemo/HeyGenAvatar.css';

const HeyGenAvatar = ({ isConnected, mediaStream }) => {
    const videoRef = useRef(null);
    const [videoError, setVideoError] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);

    // Handle video setup and playback
    const setupVideo = useCallback(async (timestamp) => {
        if (!videoRef.current || !mediaStream) return;

        try {
            // Ensure any previous stream is cleaned up
            if (videoRef.current.srcObject) {
                const oldStream = videoRef.current.srcObject;
                oldStream.getTracks().forEach(track => track.stop());
            }

            // Set initial muted state to true to help with autoplay
            videoRef.current.muted = true;

            // Attach stream
            videoRef.current.srcObject = mediaStream;
            
            // Wait for metadata to load
            await new Promise((resolve, reject) => {
                videoRef.current.onloadedmetadata = () => {
                    resolve();
                };
                videoRef.current.onerror = reject;
            });

            // Try to play with muted first
            await videoRef.current.play();
            
            // Then unmute after successful play
            videoRef.current.muted = false;
            setIsPlaying(true);
            setVideoError(null);

        } catch (error) {
            setVideoError('Failed to start video playback. Click to try again.');
            setIsPlaying(false);
        }
    }, [mediaStream]);

    useEffect(() => {
        if (isConnected && mediaStream && videoRef.current) {
            setupVideo(new Date().toISOString());
        }

        // Store video ref in a variable for cleanup
        const currentVideo = videoRef.current;

        // Cleanup function
        return () => {
            if (currentVideo?.srcObject) {
                const stream = currentVideo.srcObject;
                stream.getTracks().forEach(track => track.stop());
                currentVideo.srcObject = null;
            }
            setIsPlaying(false);
            setVideoError(null);
        };
    }, [isConnected, mediaStream, setupVideo]);

    const handleVideoClick = async () => {
        if (!videoRef.current || !mediaStream || isPlaying) return;

        await setupVideo(new Date().toISOString());
    };

    return (
        <div className="heygen-avatar-container">
            {!isConnected && (
                <div className="connection-status">
                    Connecting to avatar service...
                </div>
            )}
            {videoError && (
                <div className="video-error-overlay" onClick={handleVideoClick}>
                    <div className="error-message">
                        {videoError}
                        {!isPlaying && <div className="click-to-play">Click to try playing</div>}
                    </div>
                </div>
            )}
            <video
                ref={videoRef}
                className="heygen-avatar-video"
                autoPlay
                playsInline
                style={{
                    cursor: !isPlaying ? 'pointer' : 'default',
                    display: isPlaying ? 'block' : 'none'
                }}
                onClick={handleVideoClick}
            />
        </div>
    );
};

export default HeyGenAvatar; 